import type { FormEvent, KeyboardEvent } from 'react'
import { useEffect, useState } from 'react'
import { AvatarWithUpload } from '@/components/ui/avatar-with-upload'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  useUpdateProfileMutation,
  useUpdateUserTimezoneMutation,
} from '@/hooks/mutations/useUserProfileMutations'
import { useUserProfileImageQuery } from '@/hooks/queries/useUserProfileQuery'
import { useConfirmDialog } from '@/hooks/useConfirmDialog'
import { photoApi } from '@/lib/api/photo.api'
import { profileImageCache } from '@/lib/services/profile-image-cache.service'
import { cn } from '@/lib/utils'
import {
  applyBennyCompanionState,
  getBennyCompanionSettings,
  isTauri,
} from '@/lib/utils/tauri'
import { useAuth } from '@/store/auth.store'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  CameraIcon,
  CircleNotchIcon,
  ClockIcon,
  EnvelopeIcon,
  TrashIcon,
  UserIcon,
} from '@phosphor-icons/react'
import { AnimatePresence, motion } from 'framer-motion'
import { Sparkles } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'

import type { TimezoneOption } from '@scrambl/shared'
import * as UserConstants from '@scrambl/shared/types/user'

import { invalidateUserAvatarImage } from '../ui/user-avatar'

const timezones = UserConstants.ALL_TIMEZONES?.length
  ? UserConstants.ALL_TIMEZONES
  : [
      { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
      { value: 'America/Denver', label: 'Mountain Time (MT)' },
      { value: 'America/Chicago', label: 'Central Time (CT)' },
      { value: 'America/New_York', label: 'Eastern Time (ET)' },
      { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
      { value: 'Europe/Paris', label: 'Central European Time (CET)' },
    ]

const userAccountSchema = z.object({
  displayName: z
    .string()
    .min(1, 'Display name is required')
    .max(100, 'Display name must be less than 100 characters'),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  timezone: z.string().min(1, 'Timezone is required'),
})

type UserAccountFormValues = z.infer<typeof userAccountSchema>

interface MyAccountDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function MyAccountDialog({ open, onOpenChange }: MyAccountDialogProps) {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const { user, refreshUser } = useAuth()
  const { data: profileImageUrl } = useUserProfileImageQuery(user?.id, {
    enabled: !!user?.id,
  })
  const updateProfileMutation = useUpdateProfileMutation()
  const updateUserTimezoneMutation = useUpdateUserTimezoneMutation()
  const [isPhotoMarkedForRemoval, setIsPhotoMarkedForRemoval] = useState(false)
  const [pendingPhotoFile, setPendingPhotoFile] = useState<File | null>(null)
  const [pendingPhotoPreviewUrl, setPendingPhotoPreviewUrl] = useState<
    string | null
  >(null)
  const [errors, setErrors] = useState<
    Partial<Record<keyof UserAccountFormValues, string>>
  >({})
  const [isBennySaving, setIsBennySaving] = useState(false)
  const [bennyCompanionEnabled, setBennyCompanionEnabled] = useState(false)
  const [isBennyInitialized, setIsBennyInitialized] = useState(!isTauri())
  const { confirm, dialog } = useConfirmDialog()
  const isDesktopApp = isTauri()

  const form = useForm<UserAccountFormValues>({
    resolver: zodResolver(userAccountSchema),
    defaultValues: {
      displayName: user?.displayName || '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      timezone:
        user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  })

  // Auto-fill display name when first/last name changes (if display name is empty)
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      const { firstName, lastName, displayName } = value

      if (
        (name === 'firstName' || name === 'lastName') &&
        !displayName?.trim()
      ) {
        let suggestion = ''
        if (firstName && lastName) {
          suggestion = `${firstName} ${lastName}`
        } else if (firstName) {
          suggestion = firstName
        } else if (lastName) {
          suggestion = lastName
        }

        if (suggestion) {
          form.setValue('displayName', suggestion)
        }
      }
    })
    return () => subscription.unsubscribe()
  }, [form])

  // Track unsaved changes
  useEffect(() => {
    const subscription = form.watch(() => {
      const currentValues = form.getValues()
      const isDifferentFromOriginal =
        (user?.displayName || '') !== currentValues.displayName ||
        (user?.firstName || '') !== currentValues.firstName ||
        (user?.lastName || '') !== currentValues.lastName ||
        (user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone) !==
          currentValues.timezone

      setHasUnsavedChanges(
        isDifferentFromOriginal ||
          isPhotoMarkedForRemoval ||
          !!pendingPhotoFile,
      )
    })
    return () => subscription.unsubscribe()
  }, [form, user, isPhotoMarkedForRemoval, pendingPhotoFile])

  // Update form values when user data is loaded or changed
  useEffect(() => {
    if (user && open) {
      form.reset(
        {
          displayName: user.displayName || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          timezone:
            user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        { keepDirty: false },
      )

      setHasUnsavedChanges(false)
      setIsPhotoMarkedForRemoval(false)
      setPendingPhotoFile(null)
      setPendingPhotoPreviewUrl(currentPreviewUrl => {
        if (currentPreviewUrl) {
          URL.revokeObjectURL(currentPreviewUrl)
        }
        return null
      })
      setErrors({})
    }
  }, [user, form, open])

  useEffect(() => {
    if (!isDesktopApp || !open) return undefined

    let cancelled = false
    void getBennyCompanionSettings().then(settings => {
      if (cancelled) return
      setBennyCompanionEnabled(settings?.enabled === true)
      setIsBennyInitialized(true)
    })

    const setupListener = async () => {
      const { listen } = await import('@tauri-apps/api/event')
      return listen('benny-companion-disabled', () => {
        if (!cancelled) setBennyCompanionEnabled(false)
      })
    }

    let unlisten: (() => void) | undefined
    void setupListener().then(listener => {
      if (cancelled) {
        listener()
        return
      }
      unlisten = listener
    })

    return () => {
      cancelled = true
      unlisten?.()
    }
  }, [isDesktopApp, open])

  const handleClose = async (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(true)
      return
    }

    if (!hasUnsavedChanges) {
      onOpenChange(false)
      return
    }

    const confirmed = await confirm({
      title: 'Discard changes?',
      description: 'You have unsaved changes. Are you sure you want to close?',
      confirmText: 'Discard changes',
      variant: 'destructive',
    })
    if (confirmed) {
      onOpenChange(false)
    }
  }

  const handleFieldChange = (
    name: keyof UserAccountFormValues,
    value: string,
  ) => {
    form.setValue(name, value)
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
  }

  const validateForm = (): boolean => {
    const values = form.getValues()
    const newErrors: Partial<Record<keyof UserAccountFormValues, string>> = {}
    if (!values.displayName?.trim()) {
      newErrors.displayName = 'Display name is required'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return
    if (!user?.id) {
      toast.error('Unable to update profile: User ID is missing')
      return
    }

    const values = form.getValues()

    try {
      setIsLoading(true)

      await updateProfileMutation.mutateAsync(values)

      if (isPhotoMarkedForRemoval && profileImageUrl) {
        try {
          await photoApi.deleteProfileImage(user.id)
          setIsPhotoMarkedForRemoval(false)
        } catch (error) {
          // Error handling is done silently since profile was already updated
        }
      }

      if (pendingPhotoFile) {
        await photoApi.uploadAndUpdateProfileImage(pendingPhotoFile)
        profileImageCache.invalidateUsers([user.id])
        photoApi.clearUserImageCache(user.id)
        invalidateUserAvatarImage(user.id)
        setPendingPhotoFile(null)
        setPendingPhotoPreviewUrl(currentPreviewUrl => {
          if (currentPreviewUrl) {
            URL.revokeObjectURL(currentPreviewUrl)
          }
          return null
        })
      } else if (isPhotoMarkedForRemoval) {
        profileImageCache.invalidateUsers([user.id])
        photoApi.clearUserImageCache(user.id)
        invalidateUserAvatarImage(user.id)
      }

      await refreshUser()
      setHasUnsavedChanges(false)
      toast.success('Profile updated')
      onOpenChange(false)
    } catch (error) {
      toast.error('Failed to update profile. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePhotoSelected = (file: File, previewUrl: string) => {
    setPendingPhotoPreviewUrl(currentPreviewUrl => {
      if (currentPreviewUrl) {
        URL.revokeObjectURL(currentPreviewUrl)
      }
      return previewUrl
    })
    setPendingPhotoFile(file)
    setIsPhotoMarkedForRemoval(false)
    setHasUnsavedChanges(true)
  }

  const handleRemovePhoto = () => {
    if (!user?.id) {
      toast.error('Unable to remove profile image: User ID is missing')
      return
    }

    setPendingPhotoFile(null)
    setPendingPhotoPreviewUrl(currentPreviewUrl => {
      if (currentPreviewUrl) {
        URL.revokeObjectURL(currentPreviewUrl)
      }
      return null
    })
    setIsPhotoMarkedForRemoval(!!profileImageUrl)
    setHasUnsavedChanges(true)
  }

  const handleTimezoneChange = async (newTimezone: string) => {
    try {
      form.setValue('timezone', newTimezone)
      setIsLoading(true)
      await updateUserTimezoneMutation.mutateAsync(newTimezone)
      toast.success('Timezone updated')

      const currentValues = form.getValues()
      const isDifferentFromOriginal =
        (user?.displayName || '') !== currentValues.displayName ||
        (user?.firstName || '') !== currentValues.firstName ||
        (user?.lastName || '') !== currentValues.lastName

      setHasUnsavedChanges(isDifferentFromOriginal)
    } catch (error) {
      toast.error('Failed to update timezone. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBennyCompanionToggle = async (checked: boolean) => {
    const previous = bennyCompanionEnabled
    setBennyCompanionEnabled(checked)
    try {
      setIsBennySaving(true)
      await applyBennyCompanionState(checked)
      toast.success(
        checked
          ? 'Benny will float on your desktop'
          : 'Desktop Benny turned off',
      )
    } catch {
      setBennyCompanionEnabled(previous)
      toast.error('Failed to update desktop Benny setting')
    } finally {
      setIsBennySaving(false)
    }
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      void onSubmit(e as unknown as FormEvent)
    }
  }

  if (!user) return null

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-[480px]">
          <form onSubmit={onSubmit} className="flex flex-col">
            {/* Header */}
            <DialogHeader className="border-b border-border/40 px-5 pb-4 pt-5">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <AvatarWithUpload
                    user={user}
                    size="lg"
                    showUpload={true}
                    uploadMode="deferred"
                    previewUrl={pendingPhotoPreviewUrl}
                    onFileSelected={handlePhotoSelected}
                    className="h-12 w-12"
                  />
                  <div className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-background bg-primary">
                    <CameraIcon className="h-2.5 w-2.5 text-primary-foreground" />
                  </div>
                </div>

                <div className="min-w-0 flex-1">
                  <DialogTitle className="text-base font-semibold">
                    My Account
                  </DialogTitle>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Your Scrambl profile across all organizations
                  </p>
                  {isPhotoMarkedForRemoval && (
                    <p className="mt-1 text-[11px] text-amber-600">
                      Photo will be removed when you save
                    </p>
                  )}
                  {pendingPhotoFile && !isPhotoMarkedForRemoval && (
                    <p className="mt-1 text-[11px] text-amber-600">
                      Photo will update when you save
                    </p>
                  )}
                  {(profileImageUrl || pendingPhotoPreviewUrl) &&
                    !isPhotoMarkedForRemoval && (
                      <button
                        type="button"
                        className="mt-1 text-[11px] text-destructive hover:underline"
                        onClick={handleRemovePhoto}
                        disabled={isLoading}
                      >
                        Remove photo
                      </button>
                    )}
                </div>
              </div>
            </DialogHeader>

            {/* Content */}
            <div className="space-y-4 px-5 py-4">
              {/* First & Last Name */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <UserIcon className="h-3 w-3" />
                    First Name
                  </span>
                  <input
                    type="text"
                    value={form.watch('firstName') || ''}
                    onChange={e =>
                      handleFieldChange('firstName', e.target.value)
                    }
                    onKeyDown={handleKeyDown}
                    placeholder="First name"
                    disabled={isLoading}
                    className={cn(
                      'h-9 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none transition-colors',
                      'focus:border-primary focus:ring-2 focus:ring-primary/20',
                      'placeholder:text-muted-foreground/40 disabled:opacity-50',
                    )}
                  />
                </div>

                <div className="space-y-1.5">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <UserIcon className="h-3 w-3" />
                    Last Name
                  </span>
                  <input
                    type="text"
                    value={form.watch('lastName') || ''}
                    onChange={e =>
                      handleFieldChange('lastName', e.target.value)
                    }
                    onKeyDown={handleKeyDown}
                    placeholder="Last name"
                    disabled={isLoading}
                    className={cn(
                      'h-9 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none transition-colors',
                      'focus:border-primary focus:ring-2 focus:ring-primary/20',
                      'placeholder:text-muted-foreground/40 disabled:opacity-50',
                    )}
                  />
                </div>
              </div>

              {/* Display Name */}
              <div className="space-y-1.5">
                <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <UserIcon className="h-3 w-3" />
                  Display Name <span className="text-red-500">*</span>
                </span>
                <input
                  type="text"
                  value={form.watch('displayName') || ''}
                  onChange={e =>
                    handleFieldChange('displayName', e.target.value)
                  }
                  onKeyDown={handleKeyDown}
                  placeholder="How others see your name"
                  disabled={isLoading}
                  className={cn(
                    'h-9 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none transition-colors',
                    'focus:border-primary focus:ring-2 focus:ring-primary/20',
                    'placeholder:text-muted-foreground/40 disabled:opacity-50',
                    errors.displayName &&
                      'border-red-500 focus:ring-red-500/20',
                  )}
                />
                <AnimatePresence>
                  {errors.displayName && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="text-[11px] text-red-500"
                    >
                      {errors.displayName}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              {/* Email (read-only) */}
              <div className="space-y-1.5">
                <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <EnvelopeIcon className="h-3 w-3" />
                  Email
                </span>
                <div className="flex h-9 items-center gap-2 rounded-lg border border-input bg-muted/30 px-3 text-sm">
                  <span className="text-muted-foreground">{user.email}</span>
                  <div className="ml-auto flex items-center gap-1.5">
                    <div className="h-1.5 w-1.5 rounded-full bg-green-500"></div>
                    <span className="text-[10px] text-muted-foreground">
                      Verified
                    </span>
                  </div>
                </div>
              </div>

              {/* Timezone */}
              <div className="space-y-1.5">
                <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <ClockIcon className="h-3 w-3" />
                  Timezone
                </span>
                <Select
                  value={form.watch('timezone') || ''}
                  onValueChange={value => {
                    handleFieldChange('timezone', value)
                    handleTimezoneChange(value)
                  }}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="max-h-[200px] overflow-y-auto">
                      {timezones.map((tz: TimezoneOption) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </div>
                  </SelectContent>
                </Select>
              </div>

              {isDesktopApp && (
                <div className="rounded-lg border border-amber-200/70 bg-gradient-to-br from-amber-50/80 via-background to-pink-50/70 p-3 shadow-sm dark:border-amber-400/20 dark:from-amber-400/10 dark:via-slate-900/80 dark:to-blue-400/10">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-400/25 text-amber-700 ring-1 ring-amber-500/20 dark:bg-amber-300/15 dark:text-amber-200 dark:ring-amber-200/20">
                        <Sparkles className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground dark:text-slate-50">
                          Floating Benny
                        </p>
                        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground dark:text-slate-300">
                          Keep Benny on top of your desktop as a draggable
                          companion.
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={bennyCompanionEnabled}
                      disabled={!isBennyInitialized || isBennySaving}
                      onCheckedChange={handleBennyCompanionToggle}
                      aria-label="Toggle floating Benny"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Delete Account Link */}
            <div className="px-5 pb-1">
              <button
                type="button"
                className="text-xs text-destructive/70 transition-colors hover:text-destructive hover:underline"
                onClick={() => {
                  onOpenChange(false)
                  navigate('/delete-account')
                }}
              >
                <TrashIcon className="relative -top-px mr-1 inline h-3 w-3" />
                Delete account
              </button>
            </div>

            {/* Footer */}
            <DialogFooter className="border-t border-border/40 bg-muted/20 px-5 py-3">
              <div className="flex w-full items-center justify-between">
                <span className="text-[11px] text-muted-foreground">
                  {hasUnsavedChanges ? '⌘+Enter to save' : ''}
                </span>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleClose(false)}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    disabled={isLoading || !hasUnsavedChanges}
                    className="min-w-[80px]"
                  >
                    {isLoading ? (
                      <>
                        <CircleNotchIcon className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {dialog}
    </>
  )
}
